import type {Connection} from '@package/shared'
import TimeAgo from 'react-timeago'
import {ActionIcon, Text} from '@mantine/core'
import {useCallback, useMemo, useState} from 'react'
import {IconRefresh} from '@tabler/icons-react'
import {useConnections} from '../../../../providers/connections/useConnections.ts'
import dayjs from 'dayjs'
import {getTimeAgoFormatter} from './formatter/getTimeAgoFormatter.ts'
import {formatLocalDateTime} from './formatter/formatLocalDateTime.ts'
import {useUserSettings} from '../../../../providers/userSettings/useUserSettings.ts'
import {useSubscription} from '../../../../providers/subscription/useSubscription.ts'
import {FeatureNotAvailableButton} from '../../../../components/featureNotAvailableButton/FeatureNotAvailableButton.tsx'

type Props = {
  connection: Connection
  disabled: boolean
}

export const LastRefresh = ({connection, disabled: containerDisabled}: Props) => {
  const {userSettings} = useUserSettings()
  const {activeSubscriptionTier} = useSubscription()
  const {refreshRecommendations} = useConnections()
  const [refreshing, setRefreshing] = useState<boolean>(false)

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    await refreshRecommendations(connection.id).finally(() => setRefreshing(false))
  }, [connection.id, refreshRecommendations])

  const lastRefreshTime = useMemo(() => {
    if (!connection.lastRefreshTime) return undefined
    const lastRefreshTime = dayjs(connection.lastRefreshTime)
    const now = dayjs()
    return lastRefreshTime.isAfter(now) ? now : lastRefreshTime
  }, [connection.lastRefreshTime])

  const disabled = containerDisabled || !activeSubscriptionTier.features.recommendationRefresh.allowManual

  return (
    <div style={{display: 'flex', alignItems: 'center', gap: 5}}>
      <Text c={'dimmed'} size={'xs'}>
        {!lastRefreshTime && 'never'}
        {lastRefreshTime && (
          <TimeAgo
            date={lastRefreshTime.toDate()}
            formatter={getTimeAgoFormatter(userSettings)}
            title={lastRefreshTime ? formatLocalDateTime(lastRefreshTime, userSettings) : undefined}
          />
        )}
      </Text>
      <ActionIcon
        variant={'subtle'}
        size={'xs'}
        onClick={async (e) => {
          e.preventDefault()
          await handleRefresh()
        }}
        title={'Refresh now'}
        loading={refreshing}
        disabled={disabled}
      >
        <IconRefresh />
      </ActionIcon>
      <FeatureNotAvailableButton
        featureName={'Manual refresh'}
        disabled={activeSubscriptionTier.features.recommendationRefresh.allowManual}
      />
    </div>
  )
}
